
import {
  bookJobAdvanced,
  createUserAddressBookItem,
  createDraft,
  editDraft,
  getDraft,
  getProfile,
  uploadJobFiles,
} from '@/api';
import { isCancel } from 'axios';
import { sendError } from '@/helpers/sentry';
import DeliveryDetails from '@/components/jobBooking/DeliveryDetails/DeliveryDetails.vue';
import Goods from '@/components/jobBooking/Goods/Goods.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import FilesTable from '@/components/UI/filesTable/FilesTable.vue';
import UploadAttachments from '@/components/UI/UploadAttachments/UploadAttachments.vue';
import CustomerDetailsFieldGroup from '@/components/fieldGroups/CustomerDetailsFieldGroup.vue';
import * as Sentry from '@sentry/vue';
import * as routeNames from '@/router/routeNames';

import { clearPhoneNumber } from '@/utils/InputProcessingUtilities';
import { statesTimeZonesList } from '@/utils/PortalConstants';
import { convertTimeStringTo24Hours } from '@/utils/InputProcessingUtilities';
import { getReferencesParamsWrapper } from '@/helpers/booking';

import { JobBookingJobData, User, UserDataAdvancedJB } from '@/models/main';
import {
  JBAdvancedStop,
  JBAdvancedStopProducts,
  JBAdvancedStops,
  JBGoodsTableData,
  JBGoodsTableDataFull,
  JBStopDates,
  UIStateJobBooking,
} from '@/models/jbAdvanced';
import { addDays, differenceInMilliseconds } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import dp from 'date-fns/parse';

import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';

import { returnOptionsList, returnOptionsTexts } from '@/utils/PortalConstants';
import { AxiosError } from 'axios';
import { getUserDataForNonStaffUsers } from '@/utils/Common';

@Component({
  components: {
    FilesTable,
    BackButton,
    Button,
    DeliveryDetails,
    Goods,
    UploadAttachments,
    CustomerDetailsFieldGroup,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
    ...mapState(['isSideMenuOpen']),
  },
  data() {
    return {
      returnOptionsList,
      returnOptionsTexts,
    };
  },
})
export default class JobBookingAdvanced extends Vue {
  public routeNames = routeNames;
  private loading = false;
  private user: User;
  private stop3 = false;
  private stopsAr: JBAdvancedStop[] = [];
  private productsAr: JBAdvancedStopProducts[] = [];
  private goods: JBGoodsTableDataFull[] = [
    {
      tableData: [
        {
          id: -1,
          model: '',
          product_id: -1,
          serial_number: '',
          quantity: 1,
          installation: '',
          returnOption: '',
          returnOptionText: '',
        },
      ],
      checkboxStairs: false,
      checkboxLoadingDock: false,
      checkboxPPE: false,
      checkboxRunup: false,
      radioTypeGoods: '',
      stairsNotes: '',
    },
  ];
  private files: File[] = [];
  private errorFromAPIResponse = '';
  private jobHasPriority = false;
  private jobIsInterState = false;
  private jobIsWhCustomerCustomer = false;
  private showFooter = true;
  private isSideMenuOpen!: boolean;

  // Recycle return option confirmation
  private jobHasRecycledProduct = false;
  private showRecycleProductDialog = false;
  private bookJobWithRecycledConfirmed = false;

  private jobIsDraft = false;
  private jobDraftData: JobBookingJobData = {
    schema_version: '1.0',
    customer_code: '',
    caller: '',
    phone: '',
    REF1: '',
    REF2: '',
    REF3: '',
    stops: [],
    products: [],
  };
  private draftIsLoading = false;
  private draftTitle = '';
  private draftTitleOrigin = '';
  private draftShowError = false;

  public uiState: UIStateJobBooking = {
    stops: [],
  };

  private userDataDefault: UserDataAdvancedJB = {
    customerCode: '',
    caller: '',
    phone_number: '',
    reference1: '',
    reference2: '',
    reference3: '',
    ref1_prompt: 'Reference 1',
    ref2_prompt: 'Reference 2',
    ref3_prompt: 'Reference 3',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
  };

  private dateTimes: JBStopDates[] = [];
  private dateTimesDefault = '';

  private statesTimeZonesList = statesTimeZonesList;

  private userData: UserDataAdvancedJB = { ...this.userDataDefault };

  private stops: JBAdvancedStops[] = [];
  private stopsDates: JBStopDates[] = [];
  private stopNames = {
    0: '',
    1: '',
    2: '',
  };

  private handleScroll(): void {
    const submitSectionHeight = document.getElementsByClassName('section-submit')[0].clientHeight;
    const pageYOffset = window.pageYOffset;
    const body = document.body,
      html = document.documentElement;
    const pageHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight,
    );
    const clientHeight = document.documentElement.clientHeight;
    pageHeight - pageYOffset - clientHeight <= submitSectionHeight
      ? (this.showFooter = false)
      : (this.showFooter = true);
  }

  private toggleStop3(): void {
    this.stop3 = !this.stop3;
    if (!this.stop3) {
      this.goods.splice(1, 1);
      this.stops.splice(2, 1);
    }
    this.formChanged();
  }

  private hideStop3(): void {
    this.stop3 = false;
    this.goods.splice(1, 1);
    this.stops.splice(2, 1);
    this.formChanged();
  }

  private setCustomerCode(customerCode: string): void {
    if (customerCode) {
      this.userData.customerCode = customerCode;
      getReferencesParamsWrapper(this.userData.customerCode, this.userData);
    }
  }

  private changeDatesRoot(dates): void {
    this.formChanged();
    this.dateTimes = dates;
    this.dateTimesCorrection();
  }

  // Updates Stop's names that are above each Goods block
  private changeStops(stops: JBAdvancedStops[]): void {
    this.formChanged();
    this.stops = [];
    this.stops = [...stops];
    if (stops[0] && stops[0].name) {
      this.stopNames[0] = stops[0].name || '';
    }
    if (stops[1] && stops[1].name) {
      this.stopNames[1] = stops[1].name || '';
    }
    if (stops[2] && stops[2].name) {
      this.stopNames[2] = stops[2].name || '';
    }
  }

  // setting priority for the job
  private changePriority(value): void {
    this.jobHasPriority = value;
  }

  // setting interstate status for the job
  private changeInterState(value): void {
    this.jobIsInterState = value;
  }

  // setting WH -> Customer -> Customer warning
  private changeWhCustomerCustomer(value): void {
    this.jobIsWhCustomerCustomer = value;
  }

  private changeGoods(goods, stop): void {
    this.formChanged();
    this.goods[stop - 1] = goods;
    // Check product with 'Recycled' return option
    this.jobHasRecycledProduct = this.goods
      .map(good => {
        return good?.tableData.some(item => {
          if (item.returnOption === 'Recycle') {
            return true;
          }
          return false;
        });
      })
      .some(hasRecycle => !!hasRecycle);
  }

  private changeFiles(files: File[]): void {
    this.formChanged();
    this.files = [...this.files, ...files];
  }

  private deleteFile(fileIndex: number): void {
    this.files.splice(fileIndex, 1);
  }

  private formChanged(): void {
    if (!this.jobIsDraft) {
      this.writeJobToLocalStorage();
    }
    if (this.errorFromAPIResponse.length > 0) {
      this.errorFromAPIResponse = '';
    }
  }

  private writeJobToLocalStorage(): void {
    const localStorageJobData = {
      userData: this.userData,
      stops: this.stops,
      goods: this.goods,
    };
    const writtenJobData = localStorage.getItem('jobData');
    if (writtenJobData !== JSON.stringify(localStorageJobData)) {
      localStorage.setItem('jobData', JSON.stringify(localStorageJobData));
    }
  }

  // LOCAL STORAGE
  private populateDataFromLocalStorage(): void {
    const writtenJobData = localStorage.getItem('jobData');
    if (writtenJobData) {
      const localStorageJobData = JSON.parse(writtenJobData);
      if (localStorageJobData.userData) {
        this.userData = localStorageJobData.userData;
      }
      if (localStorageJobData.stops) {
        this.stops = localStorageJobData.stops;
        if (this.stops.length === 3) {
          this.stop3 = true;
        }
      }
      if (localStorageJobData.goods) {
        this.goods = localStorageJobData.goods;
      }
    }
  }

  private onlyNumberAndSpaceBlur() {
    this.userData.phone_number = clearPhoneNumber(this.userData.phone_number);
  }

  /**
   * Helper function to generate JSON according to Job-web-schema JSON-schema.
   * @private
   */
  private buildJob() {
    const warehousesStops = {
      0: false,
      1: false,
      2: null,
    };
    this.stopsAr = [];
    this.productsAr = [];

    this.stops.map((stop, i) => {
      const stopStop: JBAdvancedStop = {
        time: {
          earliest: '',
          latest: '',
        },
        name: stop.name,
        suburb: stop.suburb,
        state: stop.state,
        postcode: stop.postCode,
        address_line1: stop.address_1 || '',
        address_line2: stop.address_2 || '',
        number: i + 1,
        attributes: [],
        warehouse: stop.warehouse,
        note_text: stop.notesInstruction || '',
        note_name: stop.notesName || '',
        note_phone: stop.notesPhone || '',
        note_email: '',
      };
      warehousesStops[i] = stop.warehouse;
      this.stopsAr[i] = stopStop;
    });

    this.dateTimes.map((date: JBStopDates, i: number) => {
      if (i < this.stops.length) {
        this.stopsAr[i].time = {
          earliest: '',
          latest: '',
        };
        const stopState = this.stopsAr[i].state;
        const timeZone = stopState ? this.statesTimeZonesList[stopState].timezone : 'Australia/Sydney';
        const zoneString = format(utcToZonedTime(new Date(date.date), timeZone), 'xxxxx', {
          timeZone,
        });
        if (date.timeIsSetted) {
          this.stopsAr[i].time.earliest = date.date + 'T' + convertTimeStringTo24Hours(date.timeFrom) + zoneString;
          this.stopsAr[i].time.latest = date.date + 'T' + convertTimeStringTo24Hours(date.timeTo) + zoneString;
        } else {
          let timeFrom = '';
          let timeTo = '';
          // KK Warehouse: 7am - 5pm default
          // ANY Customer address: 9am - 5pm default – (AM = 9am - 12pm, PM = 12pm - 5pm)
          if (this.stopsAr[i].warehouse) {
            timeFrom = '07:00 AM';
            timeTo = '05:00 PM';
          } else {
            timeFrom = '09:00 AM';
            timeTo = '05:00 PM';
          }
          this.stopsAr[i].time.earliest = date.date + 'T' + convertTimeStringTo24Hours(timeFrom) + zoneString;
          this.stopsAr[i].time.latest = date.date + 'T' + convertTimeStringTo24Hours(timeTo) + zoneString;
        }
      }
    });

    // Returns indexes of customer stops – ex: `[0, 2]` or `[1]`
    const getCustomerStopsIndexes = (stops): number[] => {
      return Object.keys(stops)
        .filter(index => {
          return stops[index] === false;
        })
        .map(index => parseInt(index));
    };

    const stopIndexes = getCustomerStopsIndexes(warehousesStops);

    // Setting attributes to customer stops
    const setStopAttributes = (attr: string): void => {
      stopIndexes.map(stopIdx => {
        this.stopsAr[stopIdx].attributes.push(attr);
      });
    };

    // Setting notes to customer stops
    const setNotes = (notes: string): void => {
      stopIndexes.map(stopIdx => {
        this.stopsAr[stopIdx].note_text += ` Additional details: ${notes}`;
      });
    };

    this.goods.map((good, i) => {
      if (good.checkboxLoadingDock) {
        setStopAttributes('LOW');
      }
      if (good.checkboxPPE) {
        setStopAttributes('PPE');
      }
      if (good.checkboxStairs) {
        setStopAttributes('SSC');
      }
      if (good.stairsNotes) {
        setNotes(good.stairsNotes);
      }

      if (i + 1 <= this.stops.length) {
        good.tableData.map((data: JBGoodsTableData) => {
          const stopGoods: JBAdvancedStopProducts = {
            model: data.model || '',
            description: data.description || '',
            serial: data.serial_number || '',
            quantity: data.quantity,
            quant_id: data.quant_id,
            installation: data.installation,
            returns:
              data && (data.returnOption || data.returnOption === '') ? data.returnOption.toUpperCase() : 'STORE',
            from_stop: i + 1,
            to_stop: i + 2,
            weight: 0,
            runup: good.checkboxRunup || false,
          };
          if (data.product_id) {
            stopGoods.model_id = data.product_id;
          }
          this.productsAr.push(stopGoods);
        });
      }
    });

    const jobData = [
      {
        schema_version: '1.0',
        customer_code: this.userData.customerCode,
        caller: this.userData.caller,
        phone: this.userData.phone_number,
        source: 'WEB',
        REF1: this.userData.reference1,
        REF2: this.userData.reference2,
        REF3: this.userData.reference3,
        stops: this.stopsAr,
        products: this.productsAr,
      },
    ];

    return jobData;
  }

  /**
   * Book a job via API call
   * @private
   */

  private bookValidatedJob(): void {
    this.loading = true;
    const jobData = this.buildJob();
    //TODO: Refactor each .then to ba a sep function
    bookJobAdvanced(jobData)
      .then(res => {
        // files upload
        const params = {
          rh_id: res.data.booked[0].rh_id,
          rh_date: res.data.booked[0].rh_date,
          files: this.files,
        };
        if (this.files.length > 0) {
          uploadJobFiles(params).catch(() => {
            this.$notify({
              group: 'network',
              text: 'Attachments upload failed',
              type: 'warn',
            });
          });
        }
        // save to address book
        this.stops.map((stop, idx) => {
          if (this.uiState.stops[idx]?.addressBookCheckbox) {
            createUserAddressBookItem({
              suburb: stop.suburbId,
              name: stop.name,
              line1: stop.address_1,
              line2: stop.address_2,
              line3: '',
              city: '',
              note_name: stop.notesName,
              note_phone: stop.notesPhone,
              note_email: stop.notesEmail || '',
              note_text: stop.notesInstruction,
              warehouse: stop.warehouse,
            })
              .then(res => {
                if (res.status === 201) {
                  this.$notify({
                    group: 'network',
                    text: 'The address was added to the address book',
                    type: 'success',
                    duration: 3000,
                  });
                }
              })
              .catch(() => {
                this.$notify({
                  group: 'network',
                  title: 'Error creating an address in the address book',
                  text: 'You can add it manually on the Address Book page',
                  type: 'warn',
                  duration: 3000,
                });
              });
          }
        });
        // Clear quant cart if booking came from quant page
        const fromQuantPage = this.$route.query.fromQuantPage === 'true';
        if (fromQuantPage) {
          this.$store.dispatch('moduleQuantCart/clearCart');
        }

        // Remove local storage data
        this.removeLocalStorageData();
        //
        // redirecting
        this.$router.push({
          path: `/detailed-job?job_id=${res.data.booked[0].rh_id}&job_date=${res.data.booked[0].rh_date}`,
        });
        this.$notify({
          group: 'network',
          clean: true,
        });
        this.$notify({
          group: 'network',
          text: 'Job booked successfully',
          type: 'success',
          duration: 3000,
        });
      })
      .catch((res: AxiosError | any) => {
        // HTTP 400 comes with error message
        if (res?.response?.status === 400 && res?.response?.data.failed.length > 0) {
          this.errorFromAPIResponse = res.response.data.failed[0].error_reason;
          Sentry.withScope(scope => {
            scope.setLevel('warning');
            Sentry.captureException(res.response?.data.failed[0].error_reason);
          });
          this.$nextTick(() => {
            const el = this.$el.querySelector('.error-response') as HTMLElement;
            if (el) {
              this.$vuetify.goTo(el, { offset: 100 });
            }
          });
        } else {
          this.$notify({
            group: 'network',
            title: 'Unknown error during job booking',
            text: notificationErrorText,
            type: 'warn',
          });
          Sentry.withScope(scope => {
            scope.setLevel('error');
            Sentry.captureException(res);
          });
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private bookJob(): void {
    const form = this.$refs.form as HTMLFormElement;
    if (form.validate()) {
      if (this.jobHasRecycledProduct && this.bookJobWithRecycledConfirmed) {
        this.bookValidatedJob();
      } else if (
        // Show 'Recycled' return option dialog for staff users
        this.jobHasRecycledProduct &&
        !this.bookJobWithRecycledConfirmed &&
        !this.showRecycleProductDialog &&
        this.$ability.can('staff', 'Job')
      ) {
        this.showRecycleProductDialog = true;
      } else {
        this.bookValidatedJob();
      }
    } else {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.v-messages.error--text:first-of-type') as HTMLElement;
        if (el) {
          this.$vuetify.goTo(el, { offset: 100 });
        }
      });
    }
  }

  private removeLocalStorageData(): void {
    localStorage.removeItem('jobData');
  }

  //Clear all the form data and localStorage data
  private clearForm(): void {
    const form = this.$refs.form as HTMLFormElement;
    form.resetValidation();
    this.stopsAr = [];
    this.stops = [];
    this.goods = [
      {
        tableData: [
          {
            id: 0,
            description: '',
            installation: '',
            model: '',
            product_id: 0,
            quantity: 1,
            returnOption: '',
            returnOptionText: returnOptionsTexts[0],
            serial_number: '',
          },
        ],
        checkboxStairs: false,
        checkboxLoadingDock: false,
        checkboxPPE: false,
        checkboxRunup: false,
        radioTypeGoods: 'radio',
        stairsNotes: '',
      },
    ];
    this.stop3 = false;
    this.changeDatesRoot(JSON.parse(this.dateTimesDefault));
    this.setBusinessDay();
    // for non-staff users only references are cleared
    if (this.$ability.can('staff', 'Job')) {
      this.userData = { ...this.userDataDefault };
    } else {
      this.userData.customerCode = '';
      this.userData.reference1 = '';
      this.userData.reference2 = '';
      this.userData.reference3 = '';
    }
    this.removeLocalStorageData();
  }

  private showSuccess(text: string): void {
    this.$notify({
      group: 'network',
      text: text,
      type: 'success',
      duration: 5000,
    });
  }

  private showError(): void {
    this.$notify({
      group: 'network',
      title: 'Error saving the draft',
      text: notificationErrorText,
      type: 'warn',
      duration: 5000,
    });
  }

  private saveDraft(): void {
    this.draftIsLoading = true;
    const jobData = this.buildJob();
    const draftId = Number(this.$route.params.id);
    const params = {
      name: this.draftTitleOrigin,
      content: jobData[0],
    };
    editDraft(draftId, params)
      .then(res => {
        if (res.status === 200) {
          this.showSuccess('Draft updated successfully');
        } else {
          this.showError();
        }
      })
      .catch(() => {
        this.showError();
      })
      .finally(() => {
        this.draftIsLoading = false;
      });
  }

  private createDraft(): void {
    if (this.draftTitle.length >= 1) {
      this.draftShowError = false;
      this.draftIsLoading = true;
      const jobData = this.buildJob();
      const params = {
        name: this.draftTitle,
        content: jobData[0],
      };
      createDraft(params)
        .then(res => {
          if (res.status === 201) {
            this.draftTitle = '';
            this.showSuccess('Draft created successfully');
          } else {
            this.showError();
          }
        })
        .catch(() => {
          this.showError();
        })
        .finally(() => {
          this.draftIsLoading = false;
        });
    } else {
      this.draftShowError = true;
    }
  }

  private populateFromDraft(): void {
    this.userData.customerCode = this.jobDraftData.customer_code;
    this.userData.caller = this.jobDraftData.caller.slice(0, 16);
    this.userData.phone_number = this.jobDraftData.phone;
    this.userData.reference1 = this.jobDraftData.REF1;
    this.userData.reference2 = this.jobDraftData.REF2;
    this.userData.reference3 = this.jobDraftData.REF3;

    const dates: JBStopDates[] = [];

    this.jobDraftData.stops.map((stop, idx) => {
      // STOPS
      this.stops[idx] = {
        address_1: stop.address_line1,
        address_2: stop.address_line2,
        id: idx,
        name: stop.name,
        notesInstruction: stop.note_text.split(' Additional details:')[0],
        notesName: stop.note_name,
        notesPhone: stop.note_phone,
        postCode: stop.postcode,
        state: stop.state,
        suburb: stop.suburb,
        suburbId: -1,
        warehouse: stop.warehouse,
      };
      // DATES
      const dateTimesDefault = JSON.parse(this.dateTimesDefault);
      try {
        dates[idx] = {
          date: format(
            utcToZonedTime(new Date(stop.time.earliest), statesTimeZonesList[stop.state].timezone),
            'yyyy-MM-dd',
            { timeZone: statesTimeZonesList[stop.state].timezone },
          ),
          id: idx,
          priority: false,
          timeIsSetted: true,
          timeFrom: format(
            utcToZonedTime(new Date(stop.time.earliest), statesTimeZonesList[stop.state].timezone),
            'hh:mm aa',
            { timeZone: statesTimeZonesList[stop.state].timezone },
          ),
          timeTo: format(
            utcToZonedTime(new Date(stop.time.latest), statesTimeZonesList[stop.state].timezone),
            'hh:mm aa',
            { timeZone: statesTimeZonesList[stop.state].timezone },
          ),
        };
      } catch (e) {
        // In case when times or State are not defined we force default times
        dates[idx] = dateTimesDefault[idx];
      }
    });
    if (dates.length === 2) {
      const dateTimesDefault = JSON.parse(this.dateTimesDefault);
      dates[2] = dateTimesDefault[2];
    }
    this.changeDatesRoot(dates);
    // 3rd stop
    if (this.stops.length === 3) {
      this.stop3 = true;
    }

    // PRODUCTS
    const goodsDataFirstStop: JBGoodsTableData[] = [];
    const goodsDataSecondStop: JBGoodsTableData[] = [];
    this.jobDraftData.products.map((product, idx) => {
      function returnValue(string) {
        if (string !== '') {
          return string[0].toUpperCase() + string.slice(1).toLowerCase();
        }
        return string;
      }

      const productData = {
        id: idx,
        description: product.description,
        from_stop: product.from_stop,
        installation: product.installation,
        // TODO: change default name & id?
        model: product.model === 'Std Finisher' ? 'AA-FINISHER' : product.model,
        product_id: product.model === 'Std Finisher' ? 6182 : product.model_id || 0,
        quantity: product.quantity,
        quant_id: product.quant_id,
        returnOption: returnValue(product.returns) || '',
        returnOptionText: returnOptionsTexts[returnOptionsList.indexOf(returnValue(product.returns))],
        serial_number: product.serial,
        to_stop: product.to_stop,
        weight: product.weight,
        runup: product.runup || false,
      };
      if (product.from_stop === 1) {
        goodsDataFirstStop[goodsDataFirstStop.length] = productData;
      }
      if (product.from_stop === 2) {
        goodsDataSecondStop[goodsDataSecondStop.length] = productData;
      }
    });

    let readFromIdx = 0;
    if (this.jobDraftData?.stops[0]?.warehouse && !this.jobDraftData?.stops[2]?.warehouse) {
      readFromIdx = 1;
    }
    if (!this.jobDraftData?.stops[0]?.warehouse && this.jobDraftData?.stops[1]?.warehouse) {
      readFromIdx = 0;
    }
    if (this.jobDraftData?.stops[0]?.warehouse && this.jobDraftData?.stops[2]?.warehouse) {
      readFromIdx = 1;
    }

    this.goods = [
      {
        tableData: goodsDataFirstStop,
        checkboxStairs: this.jobDraftData?.stops[readFromIdx]?.attributes.indexOf('SSC') >= 0 ? true : false,
        checkboxLoadingDock: this.jobDraftData?.stops[readFromIdx]?.attributes.indexOf('LOW') >= 0 ? true : false,
        checkboxPPE: this.jobDraftData?.stops[readFromIdx]?.attributes.indexOf('PPE') >= 0 ? true : false,
        checkboxRunup: this.jobDraftData?.products[0]?.runup || false,
        radioTypeGoods: 'radio',
        stairsNotes: this.jobDraftData.stops[readFromIdx].note_text.split(' Additional details:')[1],
      },
      {
        tableData: goodsDataSecondStop,
        checkboxStairs: false,
        checkboxLoadingDock: false,
        checkboxPPE: false,
        checkboxRunup: false,
        radioTypeGoods: 'radio',
        stairsNotes: '',
      },
    ];
  }

  // DATE TIMES
  private setBusinessDay(): void {
    let nextBusinessDayIndex = 1;
    if (addDays(new Date(), 1).getDay() === 6) {
      nextBusinessDayIndex = 3;
    }
    if (addDays(new Date(), 1).getDay() === 7) {
      nextBusinessDayIndex = 2;
    }
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const dateAllowed = format(utcToZonedTime(addDays(new Date(), nextBusinessDayIndex), userTimeZone), 'yyyy-MM-dd');
    this.dateTimes.forEach(item => {
      item.date = dateAllowed;
    });
  }

  private dateTimesCorrection(): void {
    if (this.dateTimes[0] && this.dateTimes[1] && this.dateTimes[1].date < this.dateTimes[0].date) {
      this.dateTimes[1].date = this.dateTimes[0].date;
    }
    if (this.dateTimes[1] && this.dateTimes[2] && this.dateTimes[2].date < this.dateTimes[1].date) {
      this.dateTimes[2].date = this.dateTimes[1].date;
    }
    let jobHasPriority = false;
    this.dateTimes.map((date, idx) => {
      // priority calculating (warning is displayed if the difference is less than 3 hours)
      const timeFrom = dp(date.timeFrom, 'hh:mm aa', new Date());
      const timeTo = dp(date.timeTo, 'hh:mm aa', new Date());
      const timeDiff = differenceInMilliseconds(timeTo, timeFrom) / 60000;
      if (timeDiff < 180 && timeDiff >= 0) {
        this.dateTimes[idx].priority = true;
        jobHasPriority = true;
      } else {
        this.dateTimes[idx].priority = false;
      }
      // setting priority for the job
      this.jobHasPriority = jobHasPriority;
    });
  }

  private created(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd'); // today
    for (let i = 0; i <= 2; i++) {
      this.dateTimes.push({
        id: i,
        date: today,
        timeIsSetted: false,
        timeFrom: '08:00 AM',
        timeTo: '04:00 PM',
        priority: false,
      });
    }
    this.dateTimesDefault = JSON.stringify(this.dateTimes);
  }

  private async mounted(): Promise<void> {
    this.setBusinessDay();
    if (this.$route.params.id) {
      this.jobIsDraft = true;
      const draftId = Number(this.$route.params.id);
      getDraft(draftId)
        .then(res => {
          this.draftTitleOrigin = res.name;
          this.jobDraftData = res.content;
          this.populateFromDraft();
        })
        .catch(err => {
          sendError(err, 'JobBookingAdvanced');
        });
    } else {
      this.populateDataFromLocalStorage();
    }
    window.addEventListener('scroll', this.handleScroll);
    if (!this.$ability.can('staff', 'Job')) {
      this.userData = await getUserDataForNonStaffUsers(this.userData, this.user);
      if (this.userData.customerCode) {
        await getReferencesParamsWrapper(this.userData.customerCode, this.userData);
      }
    }
    if (this.userData.customerCode) {
      await getReferencesParamsWrapper(this.user.customerCode, this.userData);
    }
  }

  private destroyed(): void {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
